import {
  Box,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { Components } from 'streamdown';

import { BaseEntityChip } from '../../BaseEntityChip';
import { BaseEntityChipEntityTypes } from '../../BaseEntityChip/BaseEntityChip';
import { Citation } from '../../Citation/Citation';
import { ComplianceCheck, ComplianceCheckResultType } from '../../ComplianceCheck/ComplianceCheck';
import { ComplianceCheckList } from '../../ComplianceCheckList/ComplianceCheckList';
import { PrequalificationIndicator, PrequalificationStatus } from '../../PrequalificationIndicator';
import { RiskIndicator, RiskLevel } from '../../RiskIndicator/RiskIndicator';
import { Suggestion } from '../../Suggestion/Suggestion';
import { SuggestionList } from '../../SuggestionList/SuggestionList';

const basicComponents: Components = {
  h1: (props) => <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600 }} {...props} />,
  h2: (props) => <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }} {...props} />,
  h3: (props) => (
    <Typography variant="subtitle1" component="h3" gutterBottom sx={{ mb: 2, fontWeight: 600 }} {...props} />
  ),
  h4: (props) => (
    <Typography variant="subtitle2" component="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }} {...props} />
  ),
  h5: (props) => <Typography variant="body1" component="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }} {...props} />,
  h6: (props) => <Typography variant="body2" component="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }} {...props} />,

  // Paragraphs and text - slightly reduced spacing for chat
  p: (props) => <Typography variant="body2" component="p" sx={{ mb: 1, lineHeight: 1.6 }} {...props} />,

  // Links
  // replace relative links with absolute links
  a: ({ href, children }) => (
    <Link href={href?.replace(/^\.\//, '/')} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),

  // Lists - more compact for chat
  ul: (props) => (
    <Box
      component="ul"
      sx={{ pl: 4, mb: 2, listStyle: 'disc', listStylePosition: 'outside', 'ul > ol,ul': { pl: 3 } }}
      {...props}
    />
  ),
  ol: (props) => (
    <Box
      component="ol"
      sx={{ pl: 4, mb: 2, listStyle: 'decimal', listStylePosition: 'outside', 'ol > ol,ul': { pl: 3 } }}
      {...props}
    />
  ),
  li: (props) => <Typography component="li" variant="body2" sx={{ lineHeight: 1.6 }} {...props} />,

  // Blockquotes
  blockquote: (props) => (
    <Paper
      component="blockquote"
      elevation={0}
      sx={(theme) => ({
        borderLeft: 4,
        borderColor: theme.palette.primary.main,
        pl: 2,
        pr: 2,
        py: 1,
        my: 2,
        bgcolor: theme.palette.grey[50],
        fontStyle: 'italic',
      })}
      {...props}
    />
  ),

  // Horizontal rule
  hr: () => <Divider sx={{ my: 3 }} />,

  // Tables - more compact for chat
  table: (props) => (
    <TableContainer component={Paper} elevation={0} sx={{ my: 2, border: 1, borderColor: 'divider' }}>
      <Table size="small" {...props} />
    </TableContainer>
  ),
  thead: (props) => <TableHead {...props} />,
  tbody: (props) => <TableBody {...props} />,
  tr: (props) => <TableRow {...props} />,
  th: (props) => {
    const { align, ...rest } = props;
    return (
      <TableCell
        {...rest}
        align={align === 'char' ? undefined : align}
        sx={(theme) => ({ fontWeight: 600, bgcolor: theme.palette.grey[50] })}
      />
    );
  },
  td: (props) => {
    const { align, ...rest } = props;
    return <TableCell {...rest} align={align === 'char' ? undefined : align} />;
  },

  // Strong and emphasis
  strong: (props) => <Box component="strong" fontWeight="bold" {...props} />,
  em: (props) => <Box component="em" fontStyle="italic" {...props} />,
};

// `citation` is lowercased because streamdown uses rehype-raw (parse5) which
// lowercases HTML tag names, and hast-util-sanitize then matches the allowlist
// case-sensitively against the parsed tagName (see BUG-2041).
export const allowedTags = {
  citation: ['id'],
  baseentitychip: ['entity_type', 'uuid', 'url'],
  compliancecheck: ['title', 'result', 'additional_files'],
  compliancechecklist: [],
  suggestion: ['message'],
  suggestionlist: [],
  riskindicator: ['risk_level'],
  prequalificationindicator: ['status'],
};

const customComponents: Components = {
  citation: ({ id }) => <Citation id={id as string} />,
  baseentitychip: ({ entity_type, uuid, url }) => (
    <BaseEntityChip entityType={entity_type as BaseEntityChipEntityTypes} uuid={uuid as string} url={url as string} />
  ),
  compliancecheck: ({ title, result, additional_files, children }) => (
    <ComplianceCheck
      title={title as string}
      result={result as ComplianceCheckResultType}
      additionalFiles={additional_files as string[]}
    >
      {children as ReactNode}
    </ComplianceCheck>
  ),
  compliancechecklist: ({ children }) => <ComplianceCheckList>{children as ReactNode}</ComplianceCheckList>,
  suggestion: ({ message, children }) => <Suggestion message={message as string}>{children as ReactNode}</Suggestion>,
  suggestionlist: ({ children }) => <SuggestionList>{children as ReactNode}</SuggestionList>,
  riskindicator: ({ risk_level }) => <RiskIndicator riskLevel={risk_level as RiskLevel} />,
  prequalificationindicator: ({ status }) => <PrequalificationIndicator status={status as PrequalificationStatus} />,
};

export const components = {
  ...basicComponents,
  ...customComponents,
};
